import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Zap, ShieldCheck, Clock, ArrowRight, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import TurnstileWidget from "@/components/TurnstileWidget";
import PasswordInput from "@/components/PasswordInput";

export default function Register() {
  const [params] = useSearchParams();
  // Iter303 — Two entry modes:
  //   · Default → `?role=coach|student` (existing) sets the role.
  //   · `?as=organiser` → Festival Organiser signup path. Role is
  //     forced to student under the hood, `is_festival_organiser` is
  //     flagged, and the coach-vs-player toggle is hidden so nothing
  //     drags the visitor back to the coach flow.
  const isOrganiserMode = params.get("as") === "organiser";
  // Iter469 — Hoist `next` so both the magic-login redirect and the
  // "Already have an account? Sign in" link downstream can forward
  // it, keeping the basket round-trip intact for returning users.
  const nextParam = params.get("next");
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    role: isOrganiserMode ? "student" : (params.get("role") === "coach" ? "coach" : "student"),
    is_festival_organiser: isOrganiserMode,
    // Media consent captured at signup (players only). Coaches acknowledge in
    // a follow-up screen post-signup as they need to answer BOTH the subject
    // consent AND the data-controller acknowledgement, which is a separate
    // GDPR-flavoured prompt.
    media_consent: isOrganiserMode ? "granted" : "",
  });
  // Iter312 — Organiser signups must actively agree to the festival
  // licence T&Cs before the submit button unlocks. Not shown to
  // coach / player signups (they accept the base Rally-Lab terms at Join).
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  // Players are asked to choose: fill their profile straight away, or take
  // the 48h grace window and browse the platform first. Only affects where
  // we land them post-signup — the backend flow is the same either way.
  const [finishNow, setFinishNow] = useState(true);
  const { register } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    // In organiser mode, role/consent are already forced above — don't
    // let a subsequent `?role=` flip them back.
    if (isOrganiserMode) return;
    const r = params.get("role");
    if (r === "coach" || r === "student") setForm((f) => ({ ...f, role: r }));
  }, [params, isOrganiserMode]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isOrganiserMode && form.role === "student" && !form.media_consent) {
      toast.error("Please answer the media consent question before creating your account.");
      return;
    }
    if (isOrganiserMode && !termsAccepted) {
      toast.error("Please read and accept the festival licence terms.");
      return;
    }
    // Iter79 anti-bot — only enforce when a site key is configured. In dev
    // the widget doesn't render and the backend bypasses verification too.
    if (process.env.REACT_APP_TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error("Please complete the anti-bot check.");
      return;
    }
    setLoading(true);
    try {
      const u = await register({ ...form, terms_accepted: isOrganiserMode ? termsAccepted : undefined, turnstile_token: turnstileToken });
      // Existing-email path: backend sent a magic-login link, no session created.
      if (u.email_exists_magic_sent) {
        toast.success("Check your inbox — we sent you a sign-in link.");
        // Iter469 — Forward `?next=` + organiser `?as=` so the magic-login
        // flow also returns the user to the basket.
        const loginQs = new URLSearchParams();
        if (isOrganiserMode) loginQs.set("as", "organiser");
        if (nextParam) loginQs.set("next", nextParam);
        const loginHref = loginQs.toString() ? `/login?${loginQs.toString()}` : "/login";
        nav(loginHref, { replace: true });
        return;
      }
      toast.success("Account created — check your email for a verification code.");
      // Iter303 — Organiser signups land on the festival area right
      // after email verification, not the coaching dashboard.
      // Iter467 · Fix 1 — Honour `?next=` so the basket round-trip from
      // the festival landing page returns the organiser back to the
      // basket after email verification.
      if (isOrganiserMode) {
        const nextDest = nextParam || "/festival/mine";
        nav(`/onboarding/verify-email?next=${encodeURIComponent(nextDest)}`);
        return;
      }
      // Every new signup starts unverified. ProtectedRoute will pin them to
      // /onboarding/verify-email until they enter the code. Land them there
      // directly so they don't get bounced through /dashboard first.
      nav("/onboarding/verify-email");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  const isCoach = form.role === "coach";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6 py-12 noise-bg">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <div className="w-8 h-8 bg-[#CCFF00] flex items-center justify-center"><Zap strokeWidth={2.5} className="w-5 h-5 text-black" /></div>
          <span className="h-display text-2xl tracking-tighter">RALLY LAB</span>
        </Link>
        <div className="label-mono mb-2">
          {isOrganiserMode ? "FESTIVAL ORGANISER · SIGN UP" : isCoach ? "COACH · APPLY TO JOIN" : "PLAYER · SIGN UP"}
        </div>
        <h1 className="h-display text-4xl mb-2 tracking-tighter">
          {isOrganiserMode ? "Run your festival." : isCoach ? "Apply to coach." : "Start playing."}
        </h1>
        <p className="text-sm text-[#A3A3A3] mb-8">
          {isOrganiserMode
            ? "Get access to Rally-Lab's festival tools — live court engine, public wall viewer, self-service and helper seats. Coaching tools stay hidden."
            : isCoach
              ? "Step 1 of 2: create your coach account so you can plan sessions, run drills, and track player progress. Step 2: submit certification & insurance for admin review."
              : "Join free — book coaching sessions, follow the plan your coach sets, and track your on-court progress."}
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          {!isOrganiserMode && (
            <div>
              <label className="label-mono block mb-2">I am a</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "student", l: "Player" },
                  { v: "coach", l: "Coach" },
                ].map((r) => (
                  <button key={r.v} type="button" data-testid={`role-${r.v}`} onClick={() => update("role", r.v)}
                    className={`py-3 text-xs uppercase tracking-wider border transition-colors ${
                      form.role === r.v ? "bg-[#CCFF00] text-black border-[#CCFF00] font-bold" : "border-[#262626] hover:border-white"
                    }`}>{r.l}</button>
                ))}
              </div>
            </div>
          )}
          {isOrganiserMode && (
            <div className="border border-[#CCFF00]/40 bg-[#CCFF00]/5 p-3 text-xs flex items-start gap-2" data-testid="organiser-mode-banner">
              <PartyPopper className="w-4 h-4 text-[#CCFF00] mt-0.5 shrink-0" />
              <span className="text-[#E5E5E5]">
                Festival Organiser access. You'll see festival tools only after signup — no coaching UI. Already a coach? Sign in and enable Festival Organiser from the /festival page.
              </span>
            </div>
          )}

          <div>
            <label className="label-mono block mb-2">Full name</label>
            <input data-testid="register-name" required value={form.name} onChange={(e) => update("name", e.target.value)}
              className="w-full bg-[#141414] border border-[#262626] px-4 py-3 text-sm focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]" />
          </div>
          <div>
            <label className="label-mono block mb-2">Email</label>
            <input data-testid="register-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)}
              className="w-full bg-[#141414] border border-[#262626] px-4 py-3 text-sm focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]" />
          </div>
          <div>
            <label className="label-mono block mb-2">Password</label>
            <PasswordInput data-testid="register-password" required minLength={6} value={form.password} onChange={(e) => update("password", e.target.value)}
              className="w-full bg-[#141414] border border-[#262626] px-4 py-3 text-sm focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]" />
          </div>

          {isCoach && (
            <div className="border border-[#CCFF00]/40 bg-[#CCFF00]/5 p-3 text-xs flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-[#CCFF00] mt-0.5 shrink-0" />
              <span className="text-[#E5E5E5]">
                After creating your account you&apos;ll be asked to submit certification and insurance details. An admin will review your application shortly.
              </span>
            </div>
          )}

          {!isCoach && !isOrganiserMode && (
            <div className="border border-[#262626] p-3" data-testid="finish-choice-panel">
              <div className="label-mono mb-3" style={{ color: "#CCFF00" }}>NEXT STEP</div>
              <p className="text-xs text-[#A3A3A3] mb-3 leading-relaxed">
                We&apos;ll need your postcode, DOB and phone to hook you up with a coach.
                Do it now (fastest way to get approved) or take a look around first — you can complete these details whenever suits you.
              </p>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setFinishNow(true)}
                  className="w-full text-left p-3 border transition-all flex items-center gap-3"
                  style={finishNow ? { borderColor: "#CCFF00", background: "rgba(204,255,0,0.05)" } : { borderColor: "#262626" }}
                  data-testid="finish-now"
                >
                  <div className="w-3 h-3 border-2 shrink-0" style={finishNow ? { borderColor: "#CCFF00", background: "#CCFF00" } : { borderColor: "#525252" }} />
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-wider" style={{ color: finishNow ? "#CCFF00" : "#A3A3A3" }}>Complete profile now</div>
                    <div className="text-[10px] text-[#A3A3A3]">~1 minute · goes straight to admin approval</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#A3A3A3]" />
                </button>
                <button
                  type="button"
                  onClick={() => setFinishNow(false)}
                  className="w-full text-left p-3 border transition-all flex items-center gap-3"
                  style={!finishNow ? { borderColor: "#FF4500", background: "rgba(255,69,0,0.05)" } : { borderColor: "#262626" }}
                  data-testid="finish-later"
                >
                  <div className="w-3 h-3 border-2 shrink-0" style={!finishNow ? { borderColor: "#FF4500", background: "#FF4500" } : { borderColor: "#525252" }} />
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-wider flex items-center gap-2" style={{ color: !finishNow ? "#FF4500" : "#A3A3A3" }}>
                      Browse first · finish later <Clock className="w-3 h-3" />
                    </div>
                    <div className="text-[10px] text-[#A3A3A3]">Finish whenever you like — can&apos;t book or contact coaches until you do.</div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {!isCoach && !isOrganiserMode && (
            <div className="border border-[#262626] p-3" data-testid="media-consent-panel">
              <div className="label-mono mb-2" style={{ color: "#CCFF00" }}>MEDIA CONSENT — REQUIRED</div>
              <p className="text-xs text-[#A3A3A3] mb-3 leading-relaxed">
                During some coaching sessions your coach may want to take photos or short
                video clips so you can review your technique together afterwards.
                Clips are used for analysis &amp; debriefing only — never shared publicly.
                You can change your answer any time from your profile.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "granted", l: "Yes, I consent", color: "#CCFF00" },
                  { v: "declined", l: "No, please don't", color: "#FF4500" },
                ].map((c) => (
                  <button
                    key={c.v}
                    type="button"
                    onClick={() => update("media_consent", c.v)}
                    data-testid={`media-consent-${c.v}`}
                    className="p-3 border transition-all text-xs uppercase tracking-wider text-left"
                    style={form.media_consent === c.v
                      ? { borderColor: c.color, background: `${c.color}12`, color: c.color, fontWeight: 700 }
                      : { borderColor: "#262626", color: "#A3A3A3" }}
                  >
                    {c.l}
                  </button>
                ))}
              </div>
            </div>
          )}

          <TurnstileWidget onVerify={setTurnstileToken} />
          {isOrganiserMode && (
            <label
              className="flex items-start gap-3 border border-[#262626] p-3 cursor-pointer hover:border-[#CCFF00]/60 transition-colors"
              data-testid="organiser-terms-panel"
            >
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                data-testid="organiser-terms-checkbox"
                className="mt-0.5 w-4 h-4 shrink-0 accent-[#CCFF00]"
              />
              <span className="text-xs text-[#A3A3A3] leading-relaxed">
                I've read and agree to the{" "}
                <Link
                  to="/legal/festival-terms"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="organiser-terms-link"
                  className="text-[#CCFF00] hover:underline"
                >
                  Rally-Lab festival licence terms
                </Link>
                . My login is personal and non-transferable, only one active session is
                allowed per account, and licences are non-refundable once activated.
              </span>
            </label>
          )}
          <button
            data-testid="register-submit"
            disabled={loading || (isOrganiserMode && !termsAccepted)}
            type="submit"
            className="btn-volt w-full py-4 text-sm disabled:opacity-50 mt-2"
          >
            {loading ? "Creating…" : isCoach ? "Continue to application" : "Create account"}
          </button>
        </form>
        <p className="mt-6 text-sm text-[#A3A3A3]">
          Have an account?{" "}
          <Link
            to={(() => {
              // Iter469 — Carry `?as=organiser` + `?next=` through to the
              // login route so returning users via the "Sign in" link
              // land back on the basket (Iter467) after authenticating.
              const qs = new URLSearchParams();
              if (isOrganiserMode) qs.set("as", "organiser");
              if (nextParam) qs.set("next", nextParam);
              const s = qs.toString();
              return s ? `/login?${s}` : "/login";
            })()}
            data-testid="register-signin-link"
            className="text-[#CCFF00] hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
